import React, { useState, useEffect, useCallback } from 'react';
import { Map as MapIcon, Calendar, MessageSquare, Search } from 'lucide-react';
import { workspaceApi } from '../../../api/workspaceApi';

const MapArtefactGallery = ({ workspaceId, onSelectArtefact, isLoading: externalLoading }) => {
  const [artefacts, setArtefacts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const loadArtefacts = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await workspaceApi.getWorkspaceArtefacts(workspaceId);
      // Filter to only map type artefacts
      const mapArtefacts = data.filter(art => art.type === 'map');
      setArtefacts(mapArtefacts);
    } catch (err) {
      console.error('Failed to load artefacts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (workspaceId) {
      loadArtefacts();
    }
  }, [workspaceId, loadArtefacts]);

  const filteredArtefacts = artefacts.filter(art =>
    art.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (art.chat_title && art.chat_title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const loading = isLoading || externalLoading;

  return (
    <div className="space-y-6">
      <div className="relative group">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={18} />
        <input
          type="text"
          placeholder="Search map layers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-blue-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
        />
      </div>

      {loading && artefacts.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="aspect-[16/10] rounded-[32px] bg-gray-50 dark:bg-gray-900 animate-pulse" />
          ))}
        </div>
      ) : filteredArtefacts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredArtefacts.map((art) => (
            <div
              key={art.id}
              onClick={() => onSelectArtefact && onSelectArtefact(art)}
              className="group flex flex-col bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-[32px] overflow-hidden hover:border-blue-500/50 hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 cursor-pointer"
            >
              <div className="aspect-video bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center relative border-b border-gray-50 dark:border-gray-800">
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20 group-hover:opacity-40 transition-opacity">
                  <MapIcon size={60} />
                </div>
                <div className="relative z-10 flex flex-col items-center gap-2">
                  <span className="p-3 rounded-2xl shadow-sm bg-purple-100 text-purple-600">
                    <MapIcon size={24} />
                  </span>
                </div>
              </div>
              <div className="p-5">
                <h4
                  className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-tight mb-3 line-clamp-2"
                  title={art.title}
                >
                  {art.title}
                </h4>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                    <MessageSquare size={10} />
                    <span className="text-[9px] font-bold uppercase tracking-wider line-clamp-1 max-w-[150px]">
                      {art.chat_title}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <Calendar size={10} />
                    <span className="text-[8px] font-bold uppercase tracking-widest">{art.timestamp}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-20 flex flex-col items-center justify-center text-center bg-gray-50/50 dark:bg-gray-900/30 border-2 border-dashed border-gray-100 dark:border-gray-800 rounded-[40px]">
          <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">
            {searchQuery ? "No matching map layers found" : "No map layers saved yet"}
          </p>
        </div>
      )}
    </div>
  );
};

export default MapArtefactGallery;
